import { useMemo, useState } from 'react';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../../components/ui/card';
import { ChartContainer, ChartTooltip } from '../../../components/ui/chart';
import { ChartLegend } from './rate-shop-chart/chart-legend';
import { ChartTooltip as CustomChartTooltip } from './rate-shop-chart/chart-tooltip';
import { ChartPagination } from './rate-shop-chart/date-range-selector';
import type { ChartPaginationState, RateShopData, RateType } from './shared/types/rate-shop-types';
import {
  calculateYAxisDomain,
  generateChartColors,
  getDateRangeLabel,
  transformDataForChart,
} from './shared/utils/rate-data-transformers';
import { formatRateTypeDisplay } from './shared/utils/rate-formatters';

interface RateShopChartProps {
  data: RateShopData;
  rateType: RateType;
}

// Custom dot component for sold-out rates
const CustomDot = (props: any) => {
  const { cx, cy, payload, dataKey } = props;
  const isSoldOut = payload?.[`${dataKey}_soldOut`];
  const rateValue = payload?.[dataKey];

  if (isSoldOut && cx && cy && rateValue) {
    return (
      <g>
        <circle
          cx={cx}
          cy={cy}
          r={5}
          fill='rgb(239 68 68)' // bright red
          fillOpacity={0.9} // more opaque
          stroke='rgb(239 68 68)'
          strokeWidth={2}
        />
        <text
          x={cx}
          y={cy - 12}
          textAnchor='middle'
          fontSize={12}
          fontWeight='bold'
          fill='rgb(239 68 68)'
        >
          {Math.round(rateValue)}
        </text>
      </g>
    );
  }
  return null; // Let Recharts handle normal dots (which are disabled anyway)
};

export const RateShopChart = ({ data, rateType }: RateShopChartProps) => {
  const { chartData: fullChartData, competitorInfo } = useMemo(
    () => transformDataForChart(data),
    [data],
  );

  const competitors = useMemo(() => competitorInfo.map((comp) => comp.dataKey), [competitorInfo]);
  const chartConfig = useMemo(() => generateChartColors(competitors), [competitors]);

  // Initialize pagination state
  const paginationState = useMemo((): ChartPaginationState => {
    const daysPerPage = 14;
    const totalPages = Math.max(1, Math.ceil(fullChartData.length / daysPerPage));
    return {
      currentPage: 1,
      totalPages,
      daysPerPage,
    };
  }, [fullChartData.length]);

  const [currentPage, setCurrentPage] = useState(1);

  const [visibleSeries, setVisibleSeries] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(competitors.map((competitor) => [competitor, true])),
  );

  // Sort chart data by date to ensure proper order (used by both filtering and pagination display)
  const sortedChartData = useMemo(() => {
    return [...fullChartData].sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
    );
  }, [fullChartData]);

  // Filter data based on current page (14-day chunks)
  const filteredChartData = useMemo(() => {
    const startIndex = (currentPage - 1) * paginationState.daysPerPage;
    const endIndex = currentPage * paginationState.daysPerPage;

    return sortedChartData.slice(startIndex, endIndex);
  }, [sortedChartData, currentPage, paginationState.daysPerPage]);

  const yAxisDomain = useMemo(() => {
    const visibleCompetitors = competitors.filter((competitor) => visibleSeries[competitor]);
    return calculateYAxisDomain(filteredChartData, visibleCompetitors);
  }, [filteredChartData, visibleSeries, competitors]);

  const handleLegendClick = (dataKey: string) => {
    setVisibleSeries((prev) => ({
      ...prev,
      [dataKey]: !prev[dataKey],
    }));
  };

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= paginationState.totalPages) {
      setCurrentPage(page);
    }
  };

  // Get non-client competitors for show/hide all functionality
  const nonClientCompetitors = useMemo(() => {
    return competitors.filter((competitor) => {
      const competitorData = competitorInfo.find((comp) => comp.dataKey === competitor);
      return competitorData?.type !== 'client';
    });
  }, [competitors, competitorInfo]);

  const handleShowAllCompetitors = () => {
    setVisibleSeries((prev) => {
      const updated = { ...prev };
      nonClientCompetitors.forEach((competitor) => {
        updated[competitor] = true;
      });
      return updated;
    });
  };

  const handleHideAllCompetitors = () => {
    setVisibleSeries((prev) => {
      const updated = { ...prev };
      nonClientCompetitors.forEach((competitor) => {
        updated[competitor] = false;
      });
      return updated;
    });
  };

  // Create current pagination state with updated current page
  const currentPaginationState: ChartPaginationState = {
    ...paginationState,
    currentPage,
  };

  return (
    <Card className='h-full border-0 shadow-none'>
      <CardHeader className='flex items-center gap-2 space-y-0 py-5 sm:flex-row'>
        <div className='grid flex-1 gap-1'>
          <CardTitle>Rate Spread - {formatRateTypeDisplay(rateType)}</CardTitle>
          <CardDescription>
            Hotel rates for {getDateRangeLabel(currentPage, paginationState.daysPerPage)} (Page{' '}
            {currentPage} of {paginationState.totalPages}) - click legend to hide/show hotels
          </CardDescription>
        </div>
        <ChartPagination
          paginationState={currentPaginationState}
          chartData={sortedChartData}
          onPageChange={handlePageChange}
        />
      </CardHeader>
      <CardContent className='h-[300px] pb-0 border-0'>
        <ChartContainer config={chartConfig} className='h-full w-full'>
          <ResponsiveContainer width='100%' height='100%'>
            <LineChart data={filteredChartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray='3 3' className='stroke-muted' />
              <XAxis
                dataKey='displayDate'
                className='text-xs'
                interval='preserveStartEnd'
                tick={{ fontSize: 10 }}
              />
              <YAxis
                className='text-xs'
                tick={{ fontSize: 10 }}
                tickFormatter={(value) => `$${value}`}
                domain={yAxisDomain}
              />
              <ChartTooltip
                content={(props) => (
                  <CustomChartTooltip {...props} competitorInfo={competitorInfo} />
                )}
              />
              <Legend
                content={(props) => (
                  <ChartLegend
                    {...props}
                    competitorInfo={competitorInfo}
                    visibleSeries={visibleSeries}
                    onLegendClick={handleLegendClick}
                    nonClientCompetitors={nonClientCompetitors}
                    onShowAll={handleShowAllCompetitors}
                    onHideAll={handleHideAllCompetitors}
                  />
                )}
              />
              {competitors.map((competitor) => {
                const competitorData = competitorInfo.find((comp) => comp.dataKey === competitor);
                // Use the header.title directly as it's already properly formatted
                const displayName = competitorData?.name || competitor;

                return (
                  <Line
                    key={competitor}
                    type='monotone'
                    dataKey={competitor}
                    stroke={chartConfig[competitor]?.color}
                    strokeWidth={2}
                    dot={<CustomDot />}
                    name={displayName}
                    hide={!visibleSeries[competitor]}
                  />
                );
              })}
            </LineChart>
          </ResponsiveContainer>
        </ChartContainer>
      </CardContent>
    </Card>
  );
};
